import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Typography } from '@mui/material';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import type { WithTranslation } from 'react-i18next';
import { Centered } from '@/components/styled/common.styled';

type Props = WithTranslation & {
  children: ReactNode;
};

type State = { hasError: boolean; error: Error | null };

class AppErrorBoundaryBase extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // biome-ignore lint/suspicious/noConsole: surface render failures so we can fix them
    console.error(
      '[app-error-boundary] render failed',
      error,
      info.componentStack
    );
  }

  render(): ReactNode {
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <Centered sx={{ gap: 2, textAlign: 'center', p: 4 }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />
          <Typography variant="h6">
            {t('common:errorBoundary.appTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('common:errorBoundary.appBody')}
          </Typography>
          <Box
            component="pre"
            sx={{
              width: '100%',
              maxWidth: 600,
              textAlign: 'left',
              fontSize: '0.65rem',
              lineHeight: 1.5,
              overflow: 'auto',
              maxHeight: 160,
              bgcolor: 'grey.100',
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              p: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              color: 'text.secondary',
              userSelect: 'all'
            }}
          >
            {this.state.error?.stack ?? this.state.error?.message}
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={() =>
              navigator.clipboard.writeText(
                this.state.error?.stack ?? this.state.error?.message ?? ''
              )
            }
            sx={{ mt: -1 }}
          >
            {t('common:errorBoundary.copyError')}
          </Button>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            {t('common:errorBoundary.tryAgain')}
          </Button>
        </Centered>
      );
    }
    return this.props.children;
  }
}

export const AppErrorBoundary = withTranslation('common')(AppErrorBoundaryBase);
